import '@cuspar/ui/globals.css';
import '../src/App.css';
import { i18n, initialLanguage } from '../src/i18n';

import { useEffect } from 'react';
import type { MetaFunction, LinksFunction } from 'react-router';
import { isRouteErrorResponse, Links, Meta, Outlet, Scripts, ScrollRestoration, useLocation, useRouteError } from 'react-router';
import { Toaster, TooltipProvider } from '@cuspar/ui';
import { ThemeProvider } from '../src/components/ThemeProvider';
import { Header } from '../src/components/Header/Header';
import { Footer } from '../src/components/Footer/Footer';
import { NotFound } from '../src/components/UI/NotFound';
import { AnalyticsConsentBanner } from '../src/components/UI/AnalyticsConsentBanner';
import { isPrivatePath } from '../src/analytics/clarity';
import { Sentry } from '../src/sentry';
import { DEFAULT_META } from './seo';

export const meta: MetaFunction = () => DEFAULT_META;

export const links: LinksFunction = () => [{ rel: 'icon', type: 'image/x-icon', href: '/favicon.ico' }];

export function Layout({ children }: { children: React.ReactNode }) {
  return (
    <html lang='en' suppressHydrationWarning>
      <head>
        <meta charSet='UTF-8' />
        <meta name='viewport' content='width=device-width, initial-scale=1, maximum-scale=1, viewport-fit=cover' />
        {/* Set the theme class before first paint to avoid a light-mode flash:
            the prerendered HTML carries no theme class, and ThemeProvider only
            adds it in a post-hydration effect. Mirrors the preferences store
            (key ywt-preferences with legacy vite-ui-theme fallback, default
            dark, 'system' resolved via matchMedia). */}
        <script
          dangerouslySetInnerHTML={{
            __html: `(function(){try{var t=null;try{var p=JSON.parse(localStorage.getItem('ywt-preferences'));t=p&&p.state&&p.state.theme;}catch(e){}t=t||localStorage.getItem('vite-ui-theme')||'dark';if(t==='system'){t=window.matchMedia('(prefers-color-scheme: dark)').matches?'dark':'light';}document.documentElement.classList.add(t);}catch(e){document.documentElement.classList.add('dark');}})();`,
          }}
        />
        <meta name='author' content='YourWebTech (ywt.ch)' />
        <Meta />
        <Links />
      </head>
      <body>
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

function Chrome({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();

  useEffect(() => {
    // i18n initializes in English to match the prerendered HTML; switch to the
    // visitor's captured language now (after hydration) so the first client
    // render matches the server and React doesn't throw a hydration mismatch.
    // Placed in Chrome (not Root) so it also runs when the ErrorBoundary renders.
    if (initialLanguage !== i18n.resolvedLanguage) {
      void i18n.changeLanguage(initialLanguage);
      document.documentElement.lang = initialLanguage;
    }
  }, []);

  return (
    <TooltipProvider delayDuration={300}>
      <ThemeProvider>
        {/* Customer data never reaches analytics recordings: on account, admin
            and checkout pages all text is masked. */}
        <section className='min-h-screen bg-background font-sans antialiased flex flex-col' data-clarity-mask={isPrivatePath(pathname) ? 'true' : undefined}>
          <Header />
          {children}
          <Footer />
        </section>
        <AnalyticsConsentBanner />
        <Toaster position='top-center' richColors />
      </ThemeProvider>
    </TooltipProvider>
  );
}

export default function Root() {
  return (
    <Chrome>
      <Outlet />
    </Chrome>
  );
}

export function ErrorBoundary() {
  const error = useRouteError();
  const is404 = isRouteErrorResponse(error) && error.status === 404;

  // Report real render/runtime errors to Sentry (replaces the Sentry.ErrorBoundary
  // that wrapped the app in the old main.tsx). Route-level 404s are reported as
  // one grouped warning instead — an unknown route means a broken link or a
  // routing regression (the /checkout/result incident class). Unknown routes
  // carry no route params (matched pages with customer ids render their own
  // NotFound states and are deliberately not reported here).
  useEffect(() => {
    if (!is404) {
      Sentry.captureException(error);
      return;
    }
    Sentry.captureMessage('SPA 404: unknown route rendered', {
      level: 'warning',
      fingerprint: ['spa-404'],
      tags: { spa404Path: window.location.pathname },
    });
  }, [error, is404]);

  if (is404) {
    return (
      <Chrome>
        <NotFound />
      </Chrome>
    );
  }
  return (
    <Chrome>
      <div className='flex-1 flex items-center justify-center p-6 text-center'>Something went wrong. Please refresh.</div>
    </Chrome>
  );
}
